/**
 * Firestore Service for Real-Time Updates
 *
 * Frontend service for Firebase/Firestore Web SDK.
 * Manages connections, authentication, and real-time subscriptions.
 */

import { deleteApp, type FirebaseApp, initializeApp } from 'firebase/app';
import { type Auth, connectAuthEmulator, getAuth, signInWithCustomToken } from 'firebase/auth';
import {
  collection,
  connectFirestoreEmulator,
  type DocumentData,
  type Firestore,
  getFirestore,
  onSnapshot,
  orderBy,
  type Query,
  query,
  type QueryConstraint,
  type Unsubscribe,
  where,
} from 'firebase/firestore';

import { DEFAULT_FIRESTORE_EMULATOR_URL, getFirebaseConfig } from './firebaseEnvironments';
import type { FirestoreConfig, QueryConfig, RealtimeUpdate } from './types';

/**
 * Build Firestore query constraints from QueryConfig
 */
const buildQueryConstraints = (queryConfig: QueryConfig): QueryConstraint[] => {
  const constraints: QueryConstraint[] = [];

  // Add where clauses
  if (queryConfig.where_clauses && queryConfig.where_clauses.length > 0) {
    queryConfig.where_clauses.forEach((clause) => {
      const field = clause.field as string;
      const operator = clause.operator as
        | '<'
        | '<='
        | '=='
        | '!='
        | '>='
        | '>'
        | 'array-contains'
        | 'in'
        | 'array-contains-any'
        | 'not-in';
      const value = clause.value;
      constraints.push(where(field, operator, value));
    });
  }

  // Add order by clauses
  if (queryConfig.order_by && queryConfig.order_by.length > 0) {
    queryConfig.order_by.forEach((orderConfig) => {
      const field = orderConfig.field as string;
      const direction = (orderConfig.direction as 'asc' | 'desc') || 'asc';
      constraints.push(orderBy(field, direction));
    });
  }

  return constraints;
};

/**
 * Convert Firestore document change to RealtimeUpdate
 */
const createUpdateFromChange = (change: {
  type: 'added' | 'modified' | 'removed';
  doc: {
    id: string;
    data: () => DocumentData;
  };
}): RealtimeUpdate => {
  return {
    id: change.doc.id,
    timestamp: new Date().toISOString(),
    data: {
      type: change.type,
      ...change.doc.data(),
    },
  };
};

/**
 * Determines if we're running in emulator mode
 */
const isEmulatorMode = (config: FirestoreConfig): boolean => {
  return config.host?.includes('localhost') || config.host?.includes('127.0.0.1') || false;
};

/**
 * Parse emulator host into hostname and port
 */
const parseEmulatorHost = (host: string): { hostname: string; port: number } => {
  // Remove protocol if present
  const cleanHost = host.replace(/^https?:\/\//, '');
  const [hostname, portStr] = cleanHost.split(':');
  return {
    hostname: hostname || 'localhost',
    port: portStr ? parseInt(portStr, 10) : 9097,
  };
};

const _FIRESTORE_DB_NAME = 'claims-llm';

/**
 * Service for managing Firestore connections and subscriptions
 */
class FirestoreService {
  private app: FirebaseApp | null = null;
  private firestore: Firestore | null = null;
  private auth: Auth | null = null;
  private unsubscribe: Unsubscribe | null = null;
  private isInitialized = false;

  /**
   * Initialize Firebase for PRODUCTION mode
   */
  private async initializeProduction(config: FirestoreConfig, customToken: string): Promise<void> {
    console.log('[FirestoreService] Initializing PRODUCTION mode');

    const firebaseConfig = getFirebaseConfig(config.project_id);
    if (!firebaseConfig) {
      throw new Error(
        `No Firebase configuration found for project_id: ${config.project_id}. ` +
          `Please add configuration in firebaseEnvironments.ts`
      );
    }

    this.app = initializeApp(firebaseConfig, `firestore-${Date.now()}`);
    this.auth = getAuth(this.app);

    await signInWithCustomToken(this.auth, customToken);

    this.firestore = getFirestore(this.app, _FIRESTORE_DB_NAME);
    console.log('[FirestoreService] Using database: (default)');
  }

  /**
   * Initialize Firebase for EMULATOR mode
   */
  private async initializeEmulator(config: FirestoreConfig, customToken: string): Promise<void> {
    console.log('[FirestoreService] Initializing EMULATOR mode');

    // Emulators don't validate API key
    const firebaseConfig = {
      apiKey: 'fake-api-key',
      projectId: config.project_id,
    };

    this.app = initializeApp(firebaseConfig, `firestore-${Date.now()}`);

    // Parse Firestore emulator host (e.g., "localhost:9097")
    const firestoreEmulatorUrl = config.host || DEFAULT_FIRESTORE_EMULATOR_URL;
    const { hostname: firestoreHost, port: firestorePort } = parseEmulatorHost(firestoreEmulatorUrl);

    // Connect to Firestore Emulator BEFORE getting auth
    this.firestore = getFirestore(this.app, _FIRESTORE_DB_NAME);
    connectFirestoreEmulator(this.firestore, firestoreHost, firestorePort);
    console.log(`[FirestoreService] Connected to Firestore emulator: ${firestoreHost}:${firestorePort}`);
    console.log('[FirestoreService] Using database: (default)');

    // Connect to Auth Emulator BEFORE authentication
    // Auth emulator is typically on Firestore port + 1 (e.g., 9098 if Firestore is 9097)
    this.auth = getAuth(this.app);
    const authEmulatorPort = firestorePort + 1; // 9097 + 1 = 9098
    connectAuthEmulator(this.auth, `http://${firestoreHost}:${authEmulatorPort}`, { disableWarnings: true });
    console.log(`[FirestoreService] Connected to Auth emulator: ${firestoreHost}:${authEmulatorPort}`);

    // Authenticate with custom token
    await signInWithCustomToken(this.auth, customToken);
    console.log('[FirestoreService] Authenticated with custom token');
  }

  /**
   * Initialize Firebase and authenticate with custom token
   */
  async initialize(config: FirestoreConfig, customToken: string): Promise<void> {
    try {
      // Clean up existing connection
      if (this.isInitialized) {
        await this.disconnect();
      }

      // Initialize based on environment
      if (isEmulatorMode(config)) {
        await this.initializeEmulator(config, customToken);
      } else {
        await this.initializeProduction(config, customToken);
      }

      this.isInitialized = true;
      console.log('[FirestoreService] Initialized successfully');
    } catch (error) {
      console.error('[FirestoreService] Initialization failed:', error);
      await this.disconnect();
      throw new Error(`Failed to initialize Firestore: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Subscribe to collection updates with query constraints
   */
  subscribe(
    collectionPath: string,
    queryConfig: QueryConfig,
    callback: (update: RealtimeUpdate) => void
  ): Unsubscribe | null {
    if (!this.isReady()) {
      console.error('[FirestoreService] Cannot subscribe: not initialized');
      return null;
    }

    try {
      // Build query with constraints
      const collectionRef = collection(this.firestore!, collectionPath);
      const constraints = buildQueryConstraints(queryConfig);
      const q: Query<DocumentData> = constraints.length > 0 ? query(collectionRef, ...constraints) : collectionRef;

      // Subscribe to snapshot changes
      this.unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          console.log('[FirestoreService] 📬 Snapshot received!');
          console.log('[FirestoreService] 🔍 Is snapshot empty?', snapshot.empty);

          snapshot.docChanges().forEach((change) => {
            console.log('[FirestoreService] 📄 Document data:', change.doc.data());
            callback(createUpdateFromChange(change));
          });
        },
        (error) => {
          console.error('[FirestoreService] ⚠️ Snapshot error - This is likely a Firestore security rules issue');
          console.error('[FirestoreService] Error details:', error);
          console.error('[FirestoreService] Error message:', error.message);

          // Check if it's a security rules error about task_id
          if (error.message?.includes('task_id')) {
            console.error(
              '[FirestoreService] 🔴 SECURITY RULES ERROR: The Firestore collection has security rules expecting "task_id" field'
            );
            console.error('[FirestoreService] 🔴 But we\'re querying chat documents that use "chat_id" instead');
            console.error('[FirestoreService] 🔴 This needs to be fixed on the AI service/Firestore side');
            console.error(
              '[FirestoreService] 🔴 The collection needs chat-specific security rules or a separate collection for chats'
            );
          }
        }
      );

      console.log('[FirestoreService] Subscription established');
      return this.unsubscribe;
    } catch (error) {
      console.error('[FirestoreService] Subscription failed:', error);
      return null;
    }
  }

  /**
   * Disconnect and clean up resources
   */
  async disconnect(): Promise<void> {
    try {
      // Unsubscribe from updates
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }

      // Sign out
      if (this.auth?.currentUser) {
        await this.auth.signOut();
      }

      // Delete Firebase app
      if (this.app) {
        await deleteApp(this.app);
        this.app = null;
      }

      this.firestore = null;
      this.auth = null;
      this.isInitialized = false;

      console.log('[FirestoreService] Disconnected');
    } catch (error) {
      console.error('[FirestoreService] Disconnect error:', error);
      this.forceCleanup();
    }
  }

  /**
   * Force cleanup on error
   */
  private forceCleanup(): void {
    this.app = null;
    this.firestore = null;
    this.auth = null;
    this.unsubscribe = null;
    this.isInitialized = false;
  }

  /**
   * Check if service is ready
   */
  isReady(): boolean {
    return this.isInitialized && this.firestore !== null;
  }
}

// Export singleton
export const firestoreService = new FirestoreService();
